import _ from 'lodash';
import { Container, Flexbox, Icon, Text } from '@morf/ui';
import { FC, memo } from 'react';
import { LoaderOverlay } from '../../../../components/shared/LoaderOverlay';
import { ProviderFilters } from '../../../../components/shared/ProviderFilters';
import { WeekListProps } from './types';
import { WeekdayDetails } from './WeekdayDetails';
import { formatMomentToValueDate } from '../../../../components/helpers/formatMomentToValueDate';
import { formatValueDateToDay } from '../../../../components/helpers/formatValueDateToDay';
import { formatValueDateToMoment } from '../../../../components/helpers/formatValueDateToMoment';
import { formatValueDateToMonth } from '../../../../components/helpers/formatValueDateToMonth';
import { goToNextWeek } from './goToNextWeek';
import { goToPrevWeek } from './goToPrevWeek';
import { useFormsortAnswers } from '../../../../components/helpers/useFormsortAnswers';
import { useSchedule } from '../../../../components/context/schedule/useSchedule';
import { useTheme } from '@morf/theming';
import { values } from '@morf/proto/values/v1/values_ts_proto';

const WeekList: FC<WeekListProps> = ({
  monthlyAvailabilitySlots,
  isLoading,
}) => {
  const theme = useTheme();
  const { isInIframe } = useFormsortAnswers();
  const { selectedDate, setSelectedDate, setIsAutoSelectedDate, setStartDate } =
    useSchedule();

  const week: values.v1.Date[] = [];
  const height: string = isLoading ? '100vh' : '100%';
  const isFindingSoonestAvailableProviders: boolean =
    isLoading || (monthlyAvailabilitySlots.length > 0 && !selectedDate);

  if (selectedDate) {
    const startMoment = formatValueDateToMoment(selectedDate).startOf('week');
    const endMoment = formatValueDateToMoment(selectedDate).endOf('week');

    while (startMoment.isSameOrBefore(endMoment)) {
      week.push(formatMomentToValueDate(startMoment));
      startMoment.add(1, 'day');
    }
  }

  const startDate: values.v1.Date = week[0];
  const endDate: values.v1.Date = week[6];

  const handleWeekChange = (weekday: values.v1.Date) => {
    setIsAutoSelectedDate(false);
    if (weekday.month !== selectedDate?.month) {
      const weekMoment = formatValueDateToMoment(weekday)
        .startOf('month')
        .startOf('week');
      setStartDate(formatMomentToValueDate(weekMoment));
    }
    setSelectedDate(weekday);
  };

  const handlePrevWeekClick = () => {
    const prevWeek = goToPrevWeek(startDate);
    handleWeekChange(prevWeek);
  };

  const handleNextWeekClick = () => {
    const nextWeek = goToNextWeek(startDate);
    handleWeekChange(nextWeek);
  };

  const header: JSX.Element =
    startDate && endDate ? (
      <Flexbox
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        height='auto'
      >
        <Container width='auto' height='auto'>
          <Text tag='h3' whiteSpace='nowrap'>
            {`Week of ${formatValueDateToMonth(
              startDate
            )} ${formatValueDateToDay(startDate)}-${formatValueDateToDay(
              endDate
            )}`}
          </Text>
        </Container>

        <Flexbox
          direction='row'
          justifyContent='flex-end'
          alignItems='center'
          width='auto'
          height='auto'
        >
          <Icon
            name='chevron-left'
            onClick={handlePrevWeekClick}
            stroke={theme.colors.main.primary.darker}
            size={1.25}
            cursor='pointer'
          />
          <Icon
            name='chevron-right'
            onClick={handleNextWeekClick}
            stroke={theme.colors.main.primary.darker}
            size={1.25}
            cursor='pointer'
          />
        </Flexbox>
      </Flexbox>
    ) : (
      <Text tag='h4'>No available providers</Text>
    );

  return (
    <Flexbox
      data-testid='week-list'
      direction='column'
      backgroundColor={theme.colors.ui.card}
      borderRadius={theme.container.borderRadius}
      borderColor={theme.container.borderColor}
      borderType={theme.container.borderType}
      borderWidth={theme.container.borderWidth}
      height='auto'
      gap={0}
    >
      <ProviderFilters
        content={
          isFindingSoonestAvailableProviders ? (
            <Text tag='h4'>Finding soonest available providers…</Text>
          ) : (
            header
          )
        }
      />

      <Flexbox
        direction='column'
        height={height}
        {...(!isInIframe && { position: 'relative', overflow: 'scroll' })}
        gap={0}
      >
        {isLoading ? (
          <LoaderOverlay />
        ) : (
          week.map((weekday, index) => (
            <WeekdayDetails
              isLoading={isLoading}
              key={index}
              monthlyAvailabilitySlots={monthlyAvailabilitySlots}
              weekday={weekday}
            />
          ))
        )}
      </Flexbox>
    </Flexbox>
  );
};

export const MemoizedWeekList = memo(WeekList);
